import { watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores'
import { useRoute } from 'vue-router'

interface RouteMeta {
  requireAuth?: boolean
}

export function useAuth() {
  const authStore = useAuthStore()
  const { isLogged } = storeToRefs(authStore)
  const route = useRoute()

  /**
   * Redirects the user to the login page by reloading the current page.
   *
   * This function checks if the current route requires authentication by inspecting
   * the `requireAuth` property in the route's metadata. If authentication is required,
   * the page is reloaded to clear Vuex and Pinia stores. The `authRouteGuard` will then
   * handle navigating the user to the login page, so a direct `router.push` call is not needed here.
   */
  const redirectToLoginPage = (): void => {
    if (route) {
      const { requireAuth } = route.meta as RouteMeta
      if (requireAuth) {
        window.location.reload()
      }
    }
  }

  watch(isLogged, (newValue: boolean) => {
    if (newValue !== true) {
      redirectToLoginPage()
    }
  })

  onMounted(() => {
    if (isLogged.value) {
      authStore.tokenRegeneration()
    }
  })

  return {
    isLogged,
    redirectToLoginPage
  }
}
